/**
 * src/core/color/IccProfile.ts
 *
 * Minimal ICC profile support:
 *   - Parse the 128-byte profile header.
 *   - Extract the profile description tag (desc / mluc).
 *   - Read XYZ white-point and primary matrix tags when present.
 *
 * Color math (DOM-free, pure TS):
 *   - convertRGBtoLab(r, g, b)   — sRGB [0,255] → CIE Lab [0..100, ±128]
 *   - convertLabtoRGB(L, a, b)   — CIE Lab → sRGB [0,255]
 *   - convertRGBtoCMYK(r, g, b) — sRGB → CMYK [0,100] (device-neutral)
 *   - convertCMYKtoRGB(c, m, y, k) — CMYK → sRGB
 *   - convertRGBtoGrayscale(r, g, b) — luminosity formula
 *   - convertGrayscaleToRGB(l)   — expand to RGB
 *   - convertRGBtoHSB(r, g, b)   — sRGB → HSB/HSV [0..360, 0..1, 0..1]
 *   - convertHSBtoRGB(h, s, b)   — HSB → sRGB
 */

// ---------------------------------------------------------------------------
// Public colour types
// ---------------------------------------------------------------------------

export interface LabColor { L: number; a: number; b: number; }
export interface CMYKColor { c: number; m: number; y: number; k: number; }
export interface HSBColor  { h: number; s: number; b: number; }

// ---------------------------------------------------------------------------
// Parsed ICC profile representation
// ---------------------------------------------------------------------------

export interface IccProfileData {
  /** Total byte length declared in the header. */
  size: number;
  /** 4-char preferred colour-space class, e.g. "scnr", "mntr", "prtr". */
  profileClass: string;
  /** Data colour space, e.g. "RGB ", "CMYK", "Lab ". */
  colorSpace: string;
  /** Profile connection space, e.g. "XYZ " or "Lab ". */
  pcs: string;
  /** Profile version, e.g. "2.1.0". */
  version: string;
  /** Human-readable profile description extracted from the 'desc' tag. */
  description: string;
  /** D50-normalised white-point XYZ when the wtpt tag is present, else null. */
  whitePoint: [number, number, number] | null;
  /**
   * sRGB primary matrix [rX,rY,rZ, gX,gY,gZ, bX,bY,bZ] normalised to D50
   * when rXYZ/gXYZ/bXYZ tags are present, else null.
   */
  primaryMatrix: [number,number,number, number,number,number, number,number,number] | null;
}

// ---------------------------------------------------------------------------
// Header offsets (all multi-byte fields are big-endian)
// ---------------------------------------------------------------------------

const HDR_SIZE          = 0;   // u32: total byte count
const HDR_CMM_TYPE      = 4;   // u32 (ignore)
const HDR_VERSION       = 8;   // u8.u8.u8 (major, minor, patch)
const HDR_CLASS         = 12;  // 4 chars: profile class
const HDR_COLOUR_SPACE  = 16;  // 4 chars: data colour space
const HDR_PCS           = 20;  // 4 chars: profile connection space

function read4(buf: Uint8Array, off: number): number {
  return (buf[off]! << 24 | buf[off+1]! << 16 | buf[off+2]! << 8 | buf[off+3]!) >>> 0;
}

function read2(buf: Uint8Array, off: number): number {
  return (buf[off]! << 8 | buf[off+1]!) >>> 0;
}

function readS15Fixed16(buf: Uint8Array, off: number): number {
  const hi = (buf[off]! << 8 | buf[off+1]!);
  const lo  = (buf[off+2]! << 8 | buf[off+3]!);
  // signed 15.16 fixed-point
  const signed = hi > 32767 ? hi - 65536 : hi;
  return signed + lo / 65536;
}

function readStr4(buf: Uint8Array, off: number): string {
  let s = "";
  for (let i = 0; i < 4; i++) s += String.fromCharCode(buf[off + i]!);
  return s;
}

// ---------------------------------------------------------------------------
// Tag table
// ---------------------------------------------------------------------------

interface TagEntry { sig: string; offset: number; size: number; }

function readTagTable(buf: Uint8Array): TagEntry[] {
  if (buf.length < 132) return [];
  const count = read4(buf, 128);
  const tags: TagEntry[] = [];
  for (let i = 0; i < count; i++) {
    const base = 132 + i * 12;
    if (base + 12 > buf.length) break;
    tags.push({
      sig:    readStr4(buf, base),
      offset: read4(buf, base + 4),
      size:   read4(buf, base + 8),
    });
  }
  return tags;
}

function findTag(tags: TagEntry[], sig: string): TagEntry | undefined {
  return tags.find(t => t.sig === sig);
}

// ---------------------------------------------------------------------------
// Desc tag extraction (both ICC v2 'desc' and v4 'mluc')
// ---------------------------------------------------------------------------

function extractDesc(buf: Uint8Array, tag: TagEntry): string {
  const off = tag.offset;
  if (off + 8 > buf.length) return "";
  const type = readStr4(buf, off);

  if (type === "desc") {
    // v2 textDescriptionType: u32 type, u32 reserved, u32 asciiLen, then ASCII
    const asciiLen = read4(buf, off + 8);
    const start = off + 12;
    const end   = Math.min(start + asciiLen, buf.length);
    let s = "";
    for (let i = start; i < end; i++) {
      const c = buf[i]!;
      if (c === 0) break;
      s += String.fromCharCode(c);
    }
    return s;
  }

  if (type === "mluc") {
    // v4 multiLocalizedUnicodeType: pick first record
    const numRecs = read4(buf, off + 8);
    if (numRecs === 0) return "";
    const recSize  = read4(buf, off + 12);
    if (recSize < 12) return "";
    // Each record: 2-char lang, 2-char country, u32 len (bytes), u32 offset-from-tag-start
    const recOff = off + 16;
    const strLen = read4(buf, recOff + 4);   // length in bytes (UTF-16BE)
    const strOff = off + read4(buf, recOff + 8);
    let s = "";
    for (let i = 0; i < strLen; i += 2) {
      const cp = read2(buf, strOff + i);
      if (cp === 0) break;
      s += String.fromCharCode(cp);
    }
    return s;
  }

  return "";
}

// ---------------------------------------------------------------------------
// XYZ tag extraction (XYZType)
// ---------------------------------------------------------------------------

function extractXYZ(buf: Uint8Array, tag: TagEntry): [number,number,number] | null {
  const off = tag.offset;
  if (off + 8 + 12 > buf.length) return null;
  const type = readStr4(buf, off);
  if (type !== "XYZ ") return null;
  const X = readS15Fixed16(buf, off + 8);
  const Y = readS15Fixed16(buf, off + 12);
  const Z = readS15Fixed16(buf, off + 16);
  return [X, Y, Z];
}

// ---------------------------------------------------------------------------
// Public parse function
// ---------------------------------------------------------------------------

/**
 * Parse a raw ICC profile byte array.  Only the header + description tag are
 * guaranteed; primary-matrix and white-point are best-effort.
 */
export function parseIccProfile(data: Uint8Array): IccProfileData {
  const buf = data;
  if (buf.length < 128) {
    return {
      size: 0, profileClass: "????", colorSpace: "????",
      pcs: "????", version: "0.0.0", description: "",
      whitePoint: null, primaryMatrix: null,
    };
  }

  const size         = read4(buf, HDR_SIZE);
  const vMajor       = buf[HDR_VERSION]!;
  const vMinor       = (buf[HDR_VERSION + 1]! >> 4) & 0xf;
  const vPatch       = buf[HDR_VERSION + 1]! & 0xf;
  const version      = `${vMajor}.${vMinor}.${vPatch}`;
  const profileClass = readStr4(buf, HDR_CLASS);
  const colorSpace   = readStr4(buf, HDR_COLOUR_SPACE);
  const pcs          = readStr4(buf, HDR_PCS);

  const tags   = readTagTable(buf);
  const descTag = findTag(tags, "desc");
  const description = descTag ? extractDesc(buf, descTag) : "";

  const wtptTag = findTag(tags, "wtpt");
  const whitePoint = wtptTag ? extractXYZ(buf, wtptTag) : null;

  const rXYZ = findTag(tags, "rXYZ");
  const gXYZ = findTag(tags, "gXYZ");
  const bXYZ = findTag(tags, "bXYZ");
  let primaryMatrix: IccProfileData["primaryMatrix"] = null;
  if (rXYZ && gXYZ && bXYZ) {
    const r = extractXYZ(buf, rXYZ);
    const g = extractXYZ(buf, gXYZ);
    const b = extractXYZ(buf, bXYZ);
    if (r && g && b) {
      primaryMatrix = [r[0],r[1],r[2], g[0],g[1],g[2], b[0],b[1],b[2]];
    }
  }

  return { size, profileClass, colorSpace, pcs, version, description, whitePoint, primaryMatrix };
}

// ---------------------------------------------------------------------------
// RGB ↔ Lab conversion  (sRGB D65, adapted to D50 for ICC PCS)
// ---------------------------------------------------------------------------

// sRGB linear gamma
function srgbToLinear(c: number): number {
  const cn = c / 255;
  return cn <= 0.04045 ? cn / 12.92 : Math.pow((cn + 0.055) / 1.055, 2.4);
}

function linearToSrgb(c: number): number {
  const v = c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
  return Math.max(0, Math.min(255, Math.round(v * 255)));
}

// sRGB D65 → XYZ D65 (IEC 61966-2-1 matrix)
function rgbToXyzD65(r: number, g: number, b: number): [number,number,number] {
  const rl = srgbToLinear(r), gl = srgbToLinear(g), bl = srgbToLinear(b);
  const X = 0.4124564 * rl + 0.3575761 * gl + 0.1804375 * bl;
  const Y = 0.2126729 * rl + 0.7151522 * gl + 0.0721750 * bl;
  const Z = 0.0193339 * rl + 0.1191920 * gl + 0.9503041 * bl;
  return [X, Y, Z];
}

// XYZ D65 → RGB D65
function xyzD65ToRgb(X: number, Y: number, Z: number): [number,number,number] {
  const rl =  3.2404542 * X - 1.5371385 * Y - 0.4985314 * Z;
  const gl = -0.9692660 * X + 1.8760108 * Y + 0.0415560 * Z;
  const bl =  0.0556434 * X - 0.2040259 * Y + 1.0572252 * Z;
  return [linearToSrgb(rl), linearToSrgb(gl), linearToSrgb(bl)];
}

// D65 → D50 Bradford chromatic adaptation
// Source: http://www.brucelindbloom.com/ Bradford D65→D50 matrix.
// Verified: maps D65 white (0.95047, 1.0, 1.08883) → D50 white (0.96422, 1.0, 0.82521).
const D65_to_D50 = [
   1.0478112,  0.0228866, -0.0501270,
   0.0295424,  0.9904844, -0.0170491,
  -0.0092345,  0.0150436,  0.7521316,
];

// D50 → D65 (inverse Bradford)
// Source: http://www.brucelindbloom.com/
const D50_to_D65 = [
   0.9555766, -0.0230393,  0.0631636,
  -0.0282895,  1.0099416,  0.0210077,
   0.0122982, -0.0204830,  1.3299098,
];

function mat3x3Vec(m: number[], x: number, y: number, z: number): [number,number,number] {
  return [
    m[0]!*x + m[1]!*y + m[2]!*z,
    m[3]!*x + m[4]!*y + m[5]!*z,
    m[6]!*x + m[7]!*y + m[8]!*z,
  ];
}

// D50 reference white for ICC PCS (CIE 1931 2° observer)
const D50_X = 0.96422, D50_Y = 1.00000, D50_Z = 0.82521;

function f(t: number): number {
  return t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116;
}

function fInv(t: number): number {
  return t > 0.2069 ? t * t * t : (t - 16 / 116) / 7.787;
}

/**
 * Convert sRGB [0,255] to CIE L*a*b* (ICC D50 PCS).
 */
export function convertRGBtoLab(r: number, g: number, b: number): LabColor {
  const [Xd65, Yd65, Zd65] = rgbToXyzD65(r, g, b);
  const [X, Y, Z] = mat3x3Vec(D65_to_D50, Xd65, Yd65, Zd65);
  const fx = f(X / D50_X), fy = f(Y / D50_Y), fz = f(Z / D50_Z);
  return {
    L: 116 * fy - 16,
    a: 500 * (fx - fy),
    b: 200 * (fy - fz),
  };
}

/**
 * Convert CIE L*a*b* (ICC D50 PCS) to sRGB [0,255].
 */
export function convertLabtoRGB(L: number, a: number, b: number): { r: number; g: number; b: number } {
  const fy = (L + 16) / 116;
  const fx = a / 500 + fy;
  const fz = fy - b / 200;
  const X = D50_X * fInv(fx);
  const Y = D50_Y * fInv(fy);
  const Z = D50_Z * fInv(fz);
  const [Xd65, Yd65, Zd65] = mat3x3Vec(D50_to_D65, X, Y, Z);
  const [ri, gi, bi] = xyzD65ToRgb(Xd65, Yd65, Zd65);
  return { r: ri, g: gi, b: bi };
}

// ---------------------------------------------------------------------------
// RGB ↔ CMYK (device-neutral, no ink-limit)
// ---------------------------------------------------------------------------

/**
 * Convert sRGB [0,255] to CMYK [0,100] (percentages).
 */
export function convertRGBtoCMYK(r: number, g: number, b: number): CMYKColor {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const k = 1 - Math.max(rn, gn, bn);
  if (k >= 1) return { c: 0, m: 0, y: 0, k: 100 };
  const d = 1 - k;
  return {
    c: Math.round(((1 - rn - k) / d) * 100),
    m: Math.round(((1 - gn - k) / d) * 100),
    y: Math.round(((1 - bn - k) / d) * 100),
    k: Math.round(k * 100),
  };
}

/**
 * Convert CMYK [0,100] (percentages) to sRGB [0,255].
 */
export function convertCMYKtoRGB(c: number, m: number, y: number, k: number): { r: number; g: number; b: number } {
  const cn = c / 100, mn = m / 100, yn = y / 100, kn = k / 100;
  return {
    r: Math.round(255 * (1 - cn) * (1 - kn)),
    g: Math.round(255 * (1 - mn) * (1 - kn)),
    b: Math.round(255 * (1 - yn) * (1 - kn)),
  };
}

// ---------------------------------------------------------------------------
// RGB ↔ Grayscale
// ---------------------------------------------------------------------------

/**
 * Convert sRGB [0,255] to luminosity grayscale [0,255] using ITU-R BT.601
 * (matches Photoshop's "Desaturate" luminosity formula).
 */
export function convertRGBtoGrayscale(r: number, g: number, b: number): number {
  return Math.round(0.2989 * r + 0.5870 * g + 0.1140 * b);
}

/**
 * Expand a grayscale [0,255] value to an RGB triple.
 */
export function convertGrayscaleToRGB(l: number): { r: number; g: number; b: number } {
  const v = Math.max(0, Math.min(255, Math.round(l)));
  return { r: v, g: v, b: v };
}

// ---------------------------------------------------------------------------
// RGB ↔ HSB (HSV)
// ---------------------------------------------------------------------------

/**
 * Convert sRGB [0,255] to HSB: h in [0,360), s in [0,1], b in [0,1].
 */
export function convertRGBtoHSB(r: number, g: number, b: number): HSBColor {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === rn)      h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
    else if (max === gn) h = ((bn - rn) / d + 2) / 6;
    else                 h = ((rn - gn) / d + 4) / 6;
  }
  return { h: h * 360, s: max === 0 ? 0 : d / max, b: max };
}

/**
 * Convert HSB: h in [0,360), s in [0,1], b in [0,1] to sRGB [0,255].
 */
export function convertHSBtoRGB(h: number, s: number, v: number): { r: number; g: number; b: number } {
  const hn = ((h % 360) + 360) % 360 / 60;
  const i  = Math.floor(hn);
  const f  = hn - i;
  const p  = v * (1 - s);
  const q  = v * (1 - s * f);
  const t  = v * (1 - s * (1 - f));
  let r = 0, g = 0, b = 0;
  switch (i % 6) {
    case 0: r=v; g=t; b=p; break;
    case 1: r=q; g=v; b=p; break;
    case 2: r=p; g=v; b=t; break;
    case 3: r=p; g=q; b=v; break;
    case 4: r=t; g=p; b=v; break;
    case 5: r=v; g=p; b=q; break;
  }
  return { r: Math.round(r*255), g: Math.round(g*255), b: Math.round(b*255) };
}
